import { useRef } from 'react';
import {
  COLOR_DARK_BLUE_PRIMARY,
  COLOR_PP_INPUT_OUTLINE,
  COLOR_WHITE,
  hasValue,
} from '@r-and-a-shared-ui/utils';
import styles from './FormFieldAttachment.module.scss';
import { FormFieldProps } from '../FormField/FormField';
import { useFormContext } from 'react-hook-form';
import {
  FormFieldIconParams,
  formFieldIcons,
} from '../config/form-field-icons';
import { FormFieldTypes } from '../config/form-field-config';
import { getErrorMessageForCurrentField } from '../../helpers/form-helpers';
import { CTA } from '@r-and-a-shared-ui/common-ui';

export const getBase64 = async (fileArg: any) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(fileArg);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

export const FormFieldAttachment = (props: FormFieldProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    name,
    fieldRef,
    placeholder,
    label,
    isDisabled,
    fieldType,
    isHidden,
    hideIcon = false,
  } = props;

  const {
    register,
    formState: { errors },
    getFieldState,
    setValue,
    setError,
    clearErrors,
    watch,
  } = useFormContext();

  const { ref, ...rest } = register(name);
  const watchedValue = watch(name);

  const formFieldIconParams: FormFieldIconParams = {
    fieldHasError: hasValue(errors?.[name]),
    className: styles['form-element__icon'],
    hideIcon,
  };

  if (isHidden) {
    return null;
  }

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors(name);
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const filesize = selectedFile.size / 1024 / 1024;
      if (filesize > 10) {
        setValue(name, undefined);
        setError(name, {
          type: 'manual',
          message: 'File size exceeds the 10MB limit.',
        });
        return;
      }
      const fileObject = {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
        content: await getBase64(selectedFile),
      };
      setValue(name, fileObject);
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };
  return (
    <div
      ref={fieldRef}
      className={`${styles['form-element']} ${styles['form-input']} ${
        errors?.[name] ? styles['form-element__has-error'] : ''
      }`}
    >
      <input
        id={name}
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/jpg,
        application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
        application/vnd.oasis.opendocument.text"
        placeholder={placeholder}
        disabled={isDisabled}
        style={{ opacity: !hasValue(label) ? 1 : undefined }}
        {...rest}
        ref={e => {
          ref(e);
          inputRef.current = e;
        }}
        onChange={onFileChange}
        hidden
      />

      <CTA
        onClick={handleClick}
        label={hasValue(watchedValue) ? 'Change File' : 'Upload File'}
        backgroundColor={COLOR_WHITE}
        borderColor={COLOR_PP_INPUT_OUTLINE}
        textColor={COLOR_DARK_BLUE_PRIMARY}
      />

      {hasValue(watchedValue) && (
        <input
          type="text"
          className={styles['form-element__input']}
          value={watchedValue.name}
          onClick={handleClick}
          readOnly
        />
      )}
      {formFieldIcons[fieldType as FormFieldTypes]?.(formFieldIconParams)}
      <div className={styles['form-element__bar']}></div>
      {hasValue(label) && (
        <label className={styles['form-element__label']} htmlFor={name}>
          {label}
        </label>
      )}
      {errors?.[name] ? (
        <small className={styles['form-element__hint']}>
          {getErrorMessageForCurrentField(getFieldState, props)}
        </small>
      ) : null}
    </div>
  );
};

export default FormFieldAttachment;
